import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import LoadingComponent from '../loadingComponent';
import Authorizer from '../session/authorizer';
import { PERMISSIONS } from '../../session/authorize';
import withRouter from '../lib/withRouter';
import Service from '../../services';
import { baseUrlAdmin } from '../../store/apiConstants';
import './CallOutcomesLog.scss';

class CallOutcomesLog extends Component {
    constructor(props) {
        super(props);
        this.service = new Service();
        this.toast = React.createRef();

        this.state = {
            loading: false,
            history: [],
            totalRecords: 0,
            lazyParams: {
                first: 0,
                rows: 20,
                page: 1,
            },
            filters: {
                assignedTo: null,
                outcome: null,
                startDate: null,
                endDate: null,
                search: ''
            }
        };

        this.assignedToOptions = [
            { label: 'All Roles', value: null },
            { label: 'Principal', value: 'PRINCIPAL' },
            { label: 'Coordinator', value: 'COORDINATOR' },
            { label: 'Accounts Executive', value: 'ACCOUNT_EXECUTIVE' },
            { label: 'Teacher/HRT', value: 'TEACHER' },
            { label: 'Admin', value: 'ADMIN' }
        ];
        
        this.outcomeOptions = [
            { label: 'All Outcomes', value: null },
            { label: 'Promised', value: 'PROMISED' },
            { label: 'Paid', value: 'PAID' },
            { label: 'Connected', value: 'CONNECTED' },
            { label: 'No Answer', value: 'NO_ANSWER' },
            { label: 'Busy', value: 'BUSY' },
            { label: 'Wrong Number', value: 'WRONG_NUMBER' },
            { label: 'Refused', value: 'REFUSED' },
            { label: 'Callback Requested', value: 'CALLBACK_REQUESTED' }
        ];
    }

    componentDidMount() {
        this.fetchCallHistory();
    }

    fetchCallHistory = async () => {
        const academicYear = localStorage.getItem('userAcademicYear');

        this.setState({ loading: true });

        try {
            const { lazyParams, filters } = this.state;
            let url = `${baseUrlAdmin}/fee-decision/actions/mobile/call-history?academicYear=${academicYear}&page=${lazyParams.page}&limit=${lazyParams.rows}`;
            
            if (filters.assignedTo) {
                url += `&assignedTo=${filters.assignedTo}`;
            }
            
            if (filters.outcome) {
                url += `&outcome=${filters.outcome}`;
            }
            
            if (filters.startDate) {
                url += `&startDate=${filters.startDate.toISOString()}`;
            }
            
            if (filters.endDate) {
                url += `&endDate=${filters.endDate.toISOString()}`;
            }
            
            if (filters.search) {
                url += `&search=${encodeURIComponent(filters.search)}`;
            }

            const response = await this.service.get(url, true);

            if (response?.status && response?.res?.success) {
                this.setState({
                    history: response.res.data,
                    totalRecords: response.res.meta.total
                });
            }
        } catch (error) {
            console.error('Error fetching call history:', error);
            this.showToast('error', 'Error', 'Failed to fetch call history');
        } finally {
            this.setState({ loading: false });
        }
    }

    showToast = (severity, summary, detail) => {
        this.toast.current?.show({ severity, summary, detail, life: 3000 });
    }

    onPage = (event) => {
        this.setState({
            lazyParams: { ...this.state.lazyParams, first: event.first, rows: event.rows, page: event.page + 1 }
        }, this.fetchCallHistory);
    }

    clearFilters = () => {
        this.setState({
            filters: {
                assignedTo: null,
                outcome: null,
                startDate: null,
                endDate: null,
                search: ''
            }
        }, this.fetchCallHistory);
    }
    
    exportToCSV = () => {
        const { history } = this.state;
        
        if (!history || history.length === 0) {
            this.showToast('warn', 'No Data', 'No records to export');
            return;
        }
        
        // Prepare CSV data
        const headers = ['Date/Time', 'Executed By', 'Student', 'Enrollment', 'Class', 'Risk', 'Outcome', 'Duration (sec)', 'Remarks'];
        const rows = history.map(item => [
            new Date(item.outcome.completedAt).toLocaleString('en-GB'),
            item.completedByName || 'Unknown',
            item.studentName || 'Unknown',
            item.enrollmentNo || 'N/A',
            item.className || 'N/A',
            item.riskBand || '-',
            item.outcome.status || '-',
            item.outcome.callDuration || 0,
            item.outcome.remarks || ''
        ]);
        
        // Create CSV content
        let csvContent = headers.join(',') + '\n';
        rows.forEach(row => {
            csvContent += row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',') + '\n';
        });
        
        // Download CSV
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `call-outcomes-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        this.showToast('success', 'Success', `Exported ${history.length} records`);
    }

    // --- Column Templates ---

    studentTemplate = (rowData) => {
        return (
            <div className="student-info">
                <div className="student-name">{rowData.studentName || 'Unknown'}</div>
                <div className="student-details">
                    <span className="enrollment-no">{rowData.enrollmentNo || 'N/A'}</span>
                    {rowData.className && <span className="class-name"> | {rowData.className}</span>}
                </div>
            </div>
        );
    }

    outcomeTemplate = (rowData) => {
        const outcomeMap = {
            'PROMISED': { label: 'Promised', severity: 'success' },
            'PAID': { label: 'Paid', severity: 'success' },
            'CONNECTED': { label: 'Connected', severity: 'info' },
            'NO_ANSWER': { label: 'No Answer', severity: 'warning' },
            'BUSY': { label: 'Busy', severity: 'warning' },
            'WRONG_NUMBER': { label: 'Wrong Number', severity: 'danger' },
            'REFUSED': { label: 'Refused', severity: 'danger' },
            'CALLBACK_REQUESTED': { label: 'Callback', severity: 'info' }
        };

        const outcomeInfo = outcomeMap[rowData.outcome?.status] || { label: rowData.outcome?.status || 'Unknown', severity: 'secondary' };

        return (
            <div className="outcome-info">
                <Tag value={outcomeInfo.label} severity={outcomeInfo.severity} />
                {rowData.outcome?.remarks && (
                    <div className="outcome-remarks">{rowData.outcome.remarks}</div>
                )}
            </div>
        );
    }

    dateTemplate = (rowData) => {
        const date = new Date(rowData.outcome?.completedAt || rowData.scheduledDate);
        return (
            <div className="date-info-single-line">
                {date.toLocaleDateString('en-GB')} {date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </div>
        );
    }

    riskTemplate = (rowData) => {
        const band = rowData.riskBand?.toUpperCase();
        let severity = null;
        let style = null;

        switch (band) {
            case 'CRITICAL': style = { backgroundColor: '#FFA6BC', color: '#BF0031' }; break;
            case 'HIGH': style = { backgroundColor: '#FFEDD5', color: '#C25400' }; break; // Explicit Dark Orange
            case 'MEDIUM': style = { backgroundColor: '#FEF9C3', color: '#854D0E' }; break; // Explicit Yellow
            case 'LOW': style = { backgroundColor: '#DCFCE7', color: '#268900' }; break;
            default: style = { backgroundColor: '#E6E8EA', color: '#4B5563' };
        }
        
        return (
            <div className="risk-info">
                <Tag value={rowData.riskBand} severity={severity} style={style} />
                <div className="risk-score mt-1" style={{ fontSize: '12px', color: '#6c757d' }}>{rowData.riskScore}/100</div>
            </div>
        );
    }

    render() {
        const { loading, history, totalRecords, lazyParams, filters } = this.state;

        if (loading) {
            return <LoadingComponent />;
        }

        return (
            <div className="call-outcomes-log-container">
                <Toast ref={this.toast} position="bottom-right" />

                <Card className="history-card">
                    <div className="card-header">
                        <h2>Call Outcomes Log</h2>
                        <div className="header-actions">
                            <Authorizer permId={PERMISSIONS.FDE_EXPORT_CALL_HISTORY}>
                                <Button 
                                    label="Export CSV"
                                    icon="pi pi-download"
                                    className="p-button-outlined"
                                    onClick={this.exportToCSV}
                                    tooltip="Export call history to CSV"
                                />
                            </Authorizer>
                            <Button 
                                icon="pi pi-refresh" 
                                className="p-button-text p-button-rounded"
                                onClick={this.fetchCallHistory}
                                tooltip="Refresh"
                            />
                        </div>
                    </div>

                    <div className="filters-section">
                        <div className="filter-group">
                            <label>Role</label>
                            <Dropdown 
                                value={filters.assignedTo}
                                options={this.assignedToOptions}
                                onChange={(e) => this.setState({
                                    filters: { ...filters, assignedTo: e.value }
                                }, this.fetchCallHistory)}
                                placeholder="All Roles"
                            />
                        </div>
                        
                        <div className="filter-group">
                            <label>Outcome</label>
                            <Dropdown 
                                value={filters.outcome}
                                options={this.outcomeOptions}
                                onChange={(e) => this.setState({
                                    filters: { ...filters, outcome: e.value }
                                }, this.fetchCallHistory)}
                                placeholder="All Outcomes"
                            />
                        </div>

                        <div className="filter-group">
                            <label>Start Date</label>
                            <Calendar
                                value={filters.startDate}
                                onChange={(e) => this.setState({
                                    filters: { ...filters, startDate: e.value }
                                }, this.fetchCallHistory)}
                                dateFormat="dd/mm/yy"
                                showIcon
                                placeholder="From"
                            />
                        </div>

                        <div className="filter-group">
                            <label>End Date</label>
                            <Calendar
                                value={filters.endDate}
                                onChange={(e) => this.setState({
                                    filters: { ...filters, endDate: e.value }
                                }, this.fetchCallHistory)}
                                dateFormat="dd/mm/yy"
                                showIcon
                                placeholder="To"
                            />
                        </div>

                        <div className="filter-group">
                            <label>Search</label>
                            <InputText
                                value={filters.search}
                                onChange={(e) => this.setState({
                                    filters: { ...filters, search: e.target.value }
                                })}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        this.fetchCallHistory();
                                    }
                                }}
                                placeholder="Student name or enrollment"
                            />
                        </div>

                        <div className="filter-actions">
                            <Button 
                                label="Clear" 
                                icon="pi pi-times" 
                                className="p-button-text"
                                onClick={this.clearFilters}
                            />
                            <Button 
                                label="Search" 
                                icon="pi pi-search"
                                onClick={this.fetchCallHistory}
                            />
                        </div>
                    </div>

                    <DataTable 
                        value={history}
                        loading={loading}
                        paginator
                        rows={lazyParams.rows}
                        first={lazyParams.first}
                        totalRecords={totalRecords}
                        onPage={this.onPage}
                        lazy
                        emptyMessage="No call history found"
                        className="history-table"
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                        scrollable
                        scrollHeight="flex"
                    >
                        <Column 
                            field="studentName" 
                            header="Student" 
                            body={this.studentTemplate}
                        />
                        <Column 
                            field="riskBand" 
                            header="Risk" 
                            body={this.riskTemplate}
                            style={{ width: '100px' }}
                        />
                        <Column 
                            field="assignedTo" 
                            header="Role"
                            style={{ width: '120px' }}
                        />
                        <Column 
                            field="outcome" 
                            header="Outcome" 
                            body={this.outcomeTemplate}
                        />
                        <Column 
                            field="completedByName" 
                            header="Executed By"
                            body={(rowData) => (
                                <div className="executed-by-info">
                                    <div className="executed-by-name">
                                        {rowData.completedByName || rowData.outcome?.completedBy || 'N/A'}
                                    </div>
                                    <div className="executed-by-role">
                                        {rowData.completedByRole || (rowData.assignedTo ? rowData.assignedTo.toLowerCase() : 'N/A')}
                                    </div>
                                </div>
                            )}
                            style={{ width: '140px' }}
                        />
                        <Column 
                            field="outcome.callStartTime" 
                            header="Call Start" 
                            body={(rowData) => {
                                if (rowData.outcome?.callStartTime) {
                                    return new Date(rowData.outcome.callStartTime).toLocaleTimeString('en-IN', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    });
                                }
                                return '-';
                            }}
                            style={{ width: '100px' }}
                        />
                        <Column 
                            field="outcome.callDuration" 
                            header="Duration"
                            body={(rowData) => {
                                const duration = rowData.outcome?.callDuration || 0;
                                if (duration === 0) return '-';
                                const minutes = Math.floor(duration / 60);
                                const seconds = duration % 60;
                                return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
                            }}
                            style={{ width: '100px' }}
                        />
                        <Column 
                            field="completedAt" 
                            header="Date & Time" 
                            body={this.dateTemplate}
                            style={{ minWidth: '180px' }}
                        />
                    </DataTable>
                </Card>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    user: state.user
});

export default connect(mapStateToProps)(withRouter(CallOutcomesLog));
